import { useMemo } from 'react';
import { Autocomplete, TextField, Box, TextFieldProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { FilterChip } from '@/components/shared/FilterAndSearchPanel/styles';
import * as localState from '../state';
import { useAtom } from 'jotai';

const StyledAutocomplete = styled(Autocomplete)(() => ({
  width: '100%',
  '& .MuiAutocomplete-input:focus': {
    boxShadow: 'none',
    border: '0px solid black',
  },
}));

type Props = {
  onSearch: (value: string) => void;
  value: string;
} & TextFieldProps;

export const CustomBlockListSearch = ({
  onSearch,
  value: textFieldValue,
  ...textFieldProps
}: Props) => {
  const [customBlockCuboids, setCustomBlockCuboids] = useAtom(localState.customBlockCuboids);

  const handleDelete = (chipToDelete: unknown) => {
    const found = customBlockCuboids.find((cuboid) => cuboid.id === chipToDelete).at(0);
    if (!found || typeof found === 'string') return;

    setCustomBlockCuboids((current) => {
      const copy = current.asMutableStateMap();
      copy.delete(found.id);
      return copy.asStateMap();
    });
  };

  const selectedChips = useMemo(
    () => customBlockCuboids.map((cuboid) => ({ name: cuboid.name ?? '', id: cuboid.id })),
    [customBlockCuboids]
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <StyledAutocomplete
        multiple
        id="chips-autocomplete"
        options={selectedChips}
        freeSolo
        disableClearable
        onInputChange={(event, newInputValue) => {
          onSearch(newInputValue);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option: unknown, index) => {
            const typedOption = option as { name: string; id: string };
            return (
              <FilterChip
                {...getTagProps({ index })}
                label={typedOption.name}
                key={index}
                deleteIcon={<CloseIcon />}
                onDelete={() => handleDelete(typedOption.id)}
              />
            );
          })
        }
        value={selectedChips}
        renderInput={(parameters) => (
          <TextField
            {...textFieldProps}
            {...parameters}
            onChange={(event) => {
              onSearch(event.target.value);
            }}
            value={textFieldValue}
            variant="standard"
            placeholder={selectedChips.length > 0 ? '' : 'Search blocks'}
          />
        )}
      />
    </Box>
  );
};
